import { addDisplayName, addProps, addWrapper, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC, MouseEvent } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { ProductWithImageAndRelatedProductIds } from 'shared/src/api/types'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { branchUnlessDataIsPresent } from 'shared/src/utils/loading'
import { typedAs } from 'shared/src/utils/typedAs'

import Icon from '../Icon'
import { IconButton } from '../IconButton'
import { LABELS_STYLE, NOTES_BOLD_STYLE } from '../styles'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    height: 64,
    background: 'rgba(35, 35, 35, 0.02)',
    paddingLeft: 8,
    paddingRight: 24,
    alignItems: 'center',
    textAlign: 'left',
    cursor: 'pointer',
  },
  image: {
    flexShrink: 0,
    width: 48,
    height: 48,
    objectFit: 'cover',
    marginRight: 16,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flexGrow: 1,
  },
  text: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'rgba(54, 52, 50, 0.8)',
  },
  brand: {
    ...NOTES_BOLD_STYLE,
  },
  name: {
    ...LABELS_STYLE,
  },
  clearButton: {
    color: 'rgba(144, 115, 79, 0.56)',
    position: 'relative',
    right: -14,
  },
  chevronRightIcon: {
    color: '#CBA67A',
  },
})

interface Props {
  product:
    | ProductWithImageAndRelatedProductIds
    | Pick<ProductWithImageAndRelatedProductIds, 'id'>
  onDelete?: () => void
  onClick: () => void
  className?: string
}

const isFullProduct = (
  product:
    | ProductWithImageAndRelatedProductIds
    | Pick<ProductWithImageAndRelatedProductIds, 'id'>
): product is ProductWithImageAndRelatedProductIds => 'brandId' in product

export const Product: FC<Props> = flowMax(
  addDisplayName('Product'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addWrapper((render, { onClick, classes, className }) => (
    <div onClick={onClick} className={cx(classes.container, className)}>
      {render()}
    </div>
  )),
  addApi,
  addDataLoadingFromPromise(
    'productData',
    ({ product, api }) =>
      typedAs<Promise<ProductWithImageAndRelatedProductIds>>(
        isFullProduct(product)
          ? Promise.resolve(product)
          : api.products.get(product.id)
      ),
    ['product.id']
  ),
  branchUnlessDataIsPresent('productData'),
  addProps(({ onDelete }) => ({
    onDelete: onDelete
      ? (event: MouseEvent) => {
          event.stopPropagation()
          onDelete()
        }
      : undefined,
  })),
  ({ productData: { brandName, name, image }, onDelete, classes }) => (
    <>
      {image && <img src={image.thumbnail} alt="" className={classes.image} />}
      <div className={classes.textContainer}>
        <p className={cx(classes.text, classes.brand)}>{brandName}</p>
        <p className={cx(classes.text, classes.name)}>{name}</p>
      </div>
      {onDelete ? (
        <IconButton
          name="clear-input"
          onClick={onDelete}
          className={classes.clearButton}
          addHitSlop
        />
      ) : (
        <Icon name="chevron-right" className={classes.chevronRightIcon} />
      )}
    </>
  )
)
